import React, { createContext, useContext, useState, useEffect } from 'react';
import { calendarService } from '../services/calendarService';
import { bookingService } from '../services/bookingService';
import { pollService } from '../services/pollService';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [calendars, setCalendars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCalendars = async () => {
    try {
      setLoading(true);
      const data = await calendarService.getUserCalendars();
      setCalendars(data);
    } catch (error) {
      console.error('Error refreshing calendars:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshBookings = async () => {
    try {
      const data = await bookingService.getUserBookings();
      setBookings(data);
    } catch (error) {
      console.error('Error refreshing bookings:', error);
    }
  };

  const refreshPolls = async () => {
    try {
      const data = await pollService.getUserPolls();
      setPolls(data);
    } catch (error) {
      console.error('Error refreshing polls:', error);
    }
  };

  const addCalendar = (calendar) => {
    setCalendars(prev => [...prev, calendar]);
  };

  const updateCalendar = (updatedCalendar) => {
    setCalendars(prev => 
      prev.map(cal => cal.id === updatedCalendar.id ? updatedCalendar : cal)
    );
  };

  const deleteCalendar = (calendarId) => {
    setCalendars(prev => prev.filter(cal => cal.id !== calendarId));
  };

  const addBooking = (booking) => {
    setBookings(prev => [...prev, booking]);
  };

  const addPoll = (poll) => {
    setPolls(prev => [...prev, poll]);
  };

  const updatePoll = (updatedPoll) => {
    setPolls(prev => 
      prev.map(poll => poll.id === updatedPoll.id ? updatedPoll : poll)
    );
  };

  const deletePoll = (pollId) => {
    setPolls(prev => prev.filter(poll => poll.id !== pollId));
  };

  const value = {
    calendars,
    bookings,
    polls,
    loading,
    refreshCalendars,
    refreshBookings,
    refreshPolls,
    addCalendar,
    updateCalendar,
    deleteCalendar,
    addBooking,
    addPoll,
    updatePoll,
    deletePoll
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};